/**
 * Lists in a plain-text box — what `Form.tsx` `Textarea`'s `lists` does to the text.
 *
 * A request's description is plain text (`DraftWorkOrderSchema.scope`) and it is read
 * back as plain text everywhere it goes: the client's ledger, a decision card, the
 * studio's work order, the outbox, and every agent through MCP. So a list is written
 * **in the text**, as the lines a person would type — `• ` for a bullet, `1. ` for a
 * number — and nothing has to parse it to show it: `.prose` keeps line breaks, so it
 * reads as a list wherever it is printed. A rich-text document would have needed a
 * reader in each of those places, and a schema change to carry it.
 *
 * Two edits, both pure: a toolbar button turns the lines under the selection into a
 * list (or back), and Enter on a list line starts the next item — or, on an item with
 * nothing in it, ends the list. Anything else is the browser's own.
 */

export type ListKind = 'bullet' | 'number'

/** The text after an edit, and where the selection goes. */
export type TextEdit = { readonly text: string; readonly start: number; readonly end: number }

/** The bullet this writes. `-` and `*` are read as bullets too, because people type them. */
export const BULLET = '•'

/** One line read as a list item: how far it is indented, its marker, and how. */
type Item = {
  readonly indent: string
  /** The indent, the marker and the space after it — everything before the words. */
  readonly head: string
  readonly kind: ListKind
  /** The bullet character, for a bullet. */
  readonly bullet: string
  /** The number, for a numbered item. */
  readonly n: number
  /** `.` or `)`, for a numbered item. */
  readonly closer: string
}

/**
 * A list line: an indent, then `•`, `-` or `*`, or a number of at most three digits
 * and `.` or `)`, then a space. Three digits rather than four so that a line opening
 * on a year — "2026. A good one." — is not taken for item two thousand and twenty-six.
 */
const ITEM = /^([ \t]*)(?:([•*-])|(\d{1,3})([.)]))[ \t]+/

function itemOf(line: string): Item | null {
  const match = ITEM.exec(line)
  if (match === null) return null
  const [head, indent = '', bullet, digits, closer = '.'] = match
  if (bullet !== undefined) return { indent, head, kind: 'bullet', bullet, n: 0, closer: '' }
  return { indent, head, kind: 'number', bullet: '', n: Number(digits), closer }
}

/** The marker for item `n` of a list, in the style of `like` where there is one. */
function markerFor(kind: ListKind, n: number, like?: Item): string {
  if (kind === 'bullet') return `${like?.kind === 'bullet' ? like.bullet : BULLET} `
  return `${String(n)}${like?.kind === 'number' ? like.closer : '.'} `
}

/**
 * Where the line holding `at` starts. At 0 it is 0 whatever the text holds:
 * `lastIndexOf` clamps a negative start to 0 and would find a newline *at* 0, which is
 * the end of the first line rather than the start of it.
 */
function lineStartOf(text: string, at: number): number {
  return at <= 0 ? 0 : text.lastIndexOf('\n', at - 1) + 1
}

/** Where the line holding `at` ends, before its newline. */
function lineEndOf(text: string, at: number): number {
  const end = text.indexOf('\n', at)
  return end === -1 ? text.length : end
}

/** A line taken apart: what comes before its words, and the words. */
function partsOf(line: string): { readonly indent: string; readonly words: string } {
  const item = itemOf(line)
  if (item !== null) return { indent: item.indent, words: line.slice(item.head.length) }
  const words = line.trimStart()
  return { indent: line.slice(0, line.length - words.length), words }
}

/**
 * The lines under a selection, made a list of one kind — or, when every one of them
 * already is that kind, made plain again. A button that only ever added would leave
 * no way to take a list back out but deleting markers by hand.
 *
 * Blank lines inside a selection of several stay blank: a paragraph break in the
 * middle of a list is the writer's. One blank line on its own — the caret on an empty
 * line — gets its marker, because that is a person starting a list.
 */
export function toggleList(text: string, start: number, end: number, kind: ListKind): TextEdit {
  const from = lineStartOf(text, start)
  // A selection that ends at the very start of a line has not chosen that line.
  const last = end > start && text[end - 1] === '\n' ? end - 1 : end
  const to = lineEndOf(text, Math.max(last, from))
  const lines = text.slice(from, to).split('\n')

  const several = lines.length > 1
  const counted = lines.filter((line) => !several || line.trim() !== '')
  const undo = counted.length > 0 && counted.every((line) => itemOf(line)?.kind === kind)

  let n = 0
  const changed = lines.map((line) => {
    const { indent, words } = partsOf(line)
    if (undo) return { line: `${indent}${words}`, words }
    if (several && line.trim() === '') return { line, words }
    n += 1
    return { line: `${indent}${markerFor(kind, n)}${words}`, words }
  })

  const block = changed.map((one) => one.line).join('\n')
  const next = `${text.slice(0, from)}${block}${text.slice(to)}`

  const only = changed[0]
  if (start === end && !several && only !== undefined) {
    // A caret stays with the words it was in, moved by what the marker added or took;
    // one that was inside the old marker lands at the start of the words.
    const before = (lines[0] ?? '').length - only.words.length
    const after = only.line.length - only.words.length
    const offset = start - from
    const at = from + (offset <= before ? after : offset - before + after)
    return { text: next, start: at, end: at }
  }
  return { text: next, start: from, end: from + block.length }
}

/**
 * What Enter does on a list line, or null for the browser's own Enter.
 *
 * Null when there is a selection (Enter replaces it, as always), when the line is not
 * a list item, and when the caret is inside the marker itself. An item with nothing
 * after its marker **ends** the list — the marker is taken away and the caret stays on
 * the line — which is how every editor a client has used behaves, and is the only way
 * out of a list that does not mean deleting a marker by hand.
 *
 * A numbered list is renumbered below the new item, so pressing Enter in the middle of
 * one does not leave two items called 2.
 */
export function continueList(text: string, start: number, end: number): TextEdit | null {
  if (start !== end) return null
  const from = lineStartOf(text, start)
  const to = lineEndOf(text, start)
  const line = text.slice(from, to)
  const item = itemOf(line)
  if (item === null || start < from + item.head.length) return null

  if (line.slice(item.head.length).trim() === '') {
    const plain = `${text.slice(0, from)}${item.indent}${text.slice(to)}`
    const at = from + item.indent.length
    return { text: plain, start: at, end: at }
  }

  const inserted = `\n${item.indent}${markerFor(item.kind, item.n + 1, item)}`
  const at = start + inserted.length
  const joined = `${text.slice(0, start)}${inserted}${text.slice(start)}`
  if (item.kind === 'bullet') return { text: joined, start: at, end: at }
  return { text: renumberAfter(joined, at, item.indent, item.n + 1), start: at, end: at }
}

/**
 * The numbered items after the line holding `at`, counted on from `n` — for as long as
 * the list runs at its indent. A line indented deeper is inside an item (a sub-list, a
 * second paragraph) and is passed over unchanged; a blank line, or a line at the
 * list's indent that is not one of its items, ends it.
 */
function renumberAfter(text: string, at: number, indent: string, n: number): string {
  const cut = lineEndOf(text, at)
  if (cut === text.length) return text

  let count = n
  let running = true
  const below = text
    .slice(cut + 1)
    .split('\n')
    .map((line) => {
      if (!running) return line
      const item = itemOf(line)
      if (item?.kind === 'number' && item.indent === indent) {
        count += 1
        return `${indent}${markerFor('number', count, item)}${line.slice(item.head.length)}`
      }
      const depth = line.length - line.trimStart().length
      if (line.trim() !== '' && depth > indent.length) return line
      running = false
      return line
    })
  return `${text.slice(0, cut)}\n${below.join('\n')}`
}
